import { Component, OnInit, Input, TemplateRef, HostListener } from '@angular/core'
import { CommonModule } from '@angular/common'
import { AtomModule } from '../../atom/atom.module'
import { NzTagModule } from 'ng-zorro-antd/tag'
import { NzCollapseModule } from 'ng-zorro-antd/collapse'
import { NzDrawerModule } from 'ng-zorro-antd/drawer'
import { NzSliderModule } from 'ng-zorro-antd/slider'
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal'
import { NzIconModule } from 'ng-zorro-antd/icon'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { RouterModule } from '@angular/router'
import { FacetService } from '../../service/facet.service'
import { Facet } from '../../gql/generated'

interface FilterValue {
  id: string;
  name: string;
  code: string;
  count?: number;
}

interface AppliedFilter {
  facetName: string;
  value: FilterValue;
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [
    CommonModule,
    AtomModule,
    NzTagModule,
    NzCollapseModule,
    NzDrawerModule,
    NzSliderModule,
    NzModalModule,
    NzIconModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule
  ],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.less',
})
export class SidebarComponent implements OnInit {
  facets: Facet[] = [];
  expandedPanels: string[] = [];
  appliedFilters: AppliedFilter[] = [];
  hideIgnoredItems = false;
  totalMatches = 931;
  priceRange: [number, number] = [0, 200];
  currentModalFilter = '';
  maxVisibleFilters = 5; // Maximum number of filters to show before using "+X more"
  isSideMenuDrawerVisible = false;
  isMobileView = false;

  // Define filter sections with their icons and options
  filterSections = [
    {
      id: 'genres',
      name: 'Genres',
      icon: 'appstore',
      options: [
        { name: 'Strategy', count: 879, selected: true, icon: 'trophy' },
        { name: 'Casual', count: 422, selected: false, icon: 'smile' },
        { name: 'Action', count: 411, selected: false, icon: 'thunderbolt' },
        { name: 'Adventure', count: 147, selected: false, icon: 'compass' },
        { name: 'Simulation', count: 136, selected: false, icon: 'radar-chart' },
        { name: 'Shooter', count: 302, selected: false, icon: 'aim' },
        { name: 'RPG', count: 274, selected: false, icon: 'user' },
        { name: 'Racing', count: 183, selected: false, icon: 'dashboard' }
      ]
    },
    {
      id: 'platform',
      name: 'Platform',
      icon: 'desktop',
      options: [
        { name: 'Steam', count: 1203, selected: false, icon: 'cloud' },
        { name: 'Epic', count: 356, selected: false, icon: 'crown' },
        { name: 'Origin', count: 187, selected: false, icon: 'robot' },
        { name: 'Uplay', count: 129, selected: false, icon: 'global' },
        { name: 'GOG', count: 98, selected: false, icon: 'block' },
        { name: 'Xbox', count: 253, selected: false, icon: 'windows' },
        { name: 'PlayStation', count: 321, selected: false, icon: 'play-circle' },
        { name: 'Switch', count: 192, selected: false, icon: 'gateway' }
      ]
    },
    {
      id: 'features',
      name: 'Features',
      icon: 'star',
      options: [
        { name: 'Multiplayer', count: 532, selected: false, icon: 'team' },
        { name: 'Controller', count: 487, selected: false, icon: 'control' },
        { name: 'Singleplayer', count: 812, selected: false, icon: 'user' },
        { name: 'Cloud Saves', count: 623, selected: false, icon: 'cloud-upload' },
        { name: 'Achievements', count: 745, selected: false, icon: 'trophy' },
        { name: 'VR Support', count: 157, selected: false, icon: 'experiment' },
        { name: 'Trading Cards', count: 312, selected: false, icon: 'credit-card' },
        { name: 'Co-op', count: 418, selected: false, icon: 'usergroup-add' }
      ]
    }
  ];

  constructor(
    private facetService: FacetService,
    private modalService: NzModalService
  ) {
    this.checkScreenSize();
  }

  @HostListener('window:resize')
  checkScreenSize() {
    this.isMobileView = window.innerWidth < 768;
  }

  toggleSideMenu() {
    this.isSideMenuDrawerVisible = !this.isSideMenuDrawerVisible;
  }

  closeSideMenu() {
    this.isSideMenuDrawerVisible = false;
  }

  ngOnInit(): void {
    this.loadFacets();

    // Mock data for development with more than 5 filters to demonstrate the "+X more" functionality
    this.appliedFilters = [
      { facetName: 'Strategy', value: { id: '1', name: 'Strategy', code: 'strategy' } },
      { facetName: 'English', value: { id: '2', name: 'English', code: 'english' } },
      { facetName: 'Price', value: { id: '3', name: 'Price', code: 'price' } },
      { facetName: 'Steam', value: { id: '4', name: 'Steam', code: 'steam' } },
      { facetName: 'Multiplayer', value: { id: '5', name: 'Multiplayer', code: 'multiplayer' } },
      { facetName: 'Action', value: { id: '6', name: 'Action', code: 'action' } },
      { facetName: 'RPG', value: { id: '7', name: 'RPG', code: 'rpg' } },
      { facetName: 'Under $20', value: { id: '8', name: 'Under $20', code: 'under20' } },
    ];
  }

  loadFacets(): void {
    this.facetService.fetchFacets().subscribe(() => {
      this.facetService.facetStore$.subscribe(state => {
        this.facets = state.facets;
      });
    });
  }

  openFilterModal(filterType: string, filterTemplate: TemplateRef<any>): void {
    this.currentModalFilter = filterType;

    const filterSection = this.filterSections.find(section => section.id === filterType);
    if (!filterSection) return;

    this.modalService.create({
      nzTitle: filterSection.name,
      nzContent: filterTemplate,
      nzFooter: [
        {
          label: 'Apply',
          type: 'primary',
          className: 'alpha',
          onClick: () => {
            this.applyFilters(filterType);
            this.modalService.closeAll();
          }
        },
        {
          label: 'Cancel',
          type: 'default',
          className: 'beta',
          onClick: () => this.modalService.closeAll()
        }
      ],
      nzWidth: 850,
      nzBodyStyle: { maxHeight: '80vh', overflow: 'auto' },
      nzMaskClosable: true,
      nzClassName: 'filter-modal',
      nzClosable: false
    });
  }

  applyFilters(filterType: string): void {
    const filterSection = this.filterSections.find(section => section.id === filterType);
    if (!filterSection) return;

    // Remove any existing filters of this type
    this.appliedFilters = this.appliedFilters.filter(f =>
      !filterSection.options.some(opt => opt.name === f.facetName));

    // Add selected options to applied filters
    filterSection.options.forEach(option => {
      if (option.selected) {
        this.appliedFilters.push({
          facetName: option.name,
          value: { id: option.name.toLowerCase(), name: option.name, code: option.name.toLowerCase() }
        });
      }
    });
  }

  toggleOptionSelection(option: any): void {
    option.selected = !option.selected;
  }

  getFilterSection(id: string) {
    return this.filterSections.find(section => section.id === id);
  }

  // Helper methods for template to avoid arrow functions in bindings
  getSelectedCount(section: any): number {
    if (!section || !section.options) return 0;
    return section.options.filter(opt => opt.selected).length;
  }

  hasSelectedOptions(section: any): boolean {
    if (!section || !section.options) return false;
    return section.options.some(opt => opt.selected);
  }

  removeFilter(filter: AppliedFilter): void {
    const index = this.appliedFilters.findIndex(f =>
      f.facetName === filter.facetName && f.value.id === filter.value.id);
    if (index > -1) {
      this.appliedFilters.splice(index, 1);
    }
  }

  hasHideIgnoredFilter(): boolean {
    return this.hideIgnoredItems;
  }

  removeHideIgnoredFilter(): void {
    this.hideIgnoredItems = false;
  }

  resetFilters(): void {
    this.appliedFilters = [];
    this.hideIgnoredItems = false;
    this.priceRange = [0, 200];
  }

  getFacetByName(name: string): Facet | undefined {
    return this.facets.find(facet => facet.name.toLowerCase() === name.toLowerCase());
  }

  // Return only the visible filters
  get visibleFilters(): AppliedFilter[] {
    return this.appliedFilters.slice(0, this.maxVisibleFilters);
  }

  // Return the count of hidden filters
  get hiddenFiltersCount(): number {
    return Math.max(0, this.appliedFilters.length - this.maxVisibleFilters);
  }

}
